import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import categoriesData from '../../functions/Categories.json'; // Import the JSON data
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';

const DesktopCategories = () => {
  const cardsContainerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [bannerImage, setBannerImage] = useState('');
  const navigate = useNavigate();

  // Check if the screen is mobile-sized
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Fetch Explore India banner image from Firestore
  useEffect(() => {
    async function fetchBannerImage() {
      try {
        const docRef = doc(db, "Images", "AllImages");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data()["Explore India"] && docSnap.data()["Explore India"].url) {
          setBannerImage(docSnap.data()["Explore India"].url);
        }
      } catch (error) {
        // fallback: do nothing
      }
    }
    fetchBannerImage();
  }, []);

  const scrollLeft = () => {
    if (cardsContainerRef.current) {
      cardsContainerRef.current.scrollLeft -= 250;
    }
  };

  const scrollRight = () => {
    if (cardsContainerRef.current) {
      cardsContainerRef.current.scrollLeft += 250;
    }
  };

  const handleCardClick = (location) => {
    window.scrollTo(0, 0);
    navigate(`/destinations?location=${encodeURIComponent(location.toLowerCase())}`);
  };

  const styles = {
    outerContainer: {
      padding: isMobile ? '20px 15px' : '30px 40px',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'relative',
      paddingBottom: isMobile ? '80px' : '100px',
      marginBottom: isMobile ? '150px' : '200px',
    },
    container: {
      width: isMobile ? '95%' : '80%', // Wider on mobile
      maxWidth: '1000px',
      margin: '0 auto',
      backgroundColor: '#fff',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      borderRadius: '12px',
      position: 'relative',
      zIndex: 1,
    },
    banner: {
      backgroundImage: bannerImage
        ? `url('${bannerImage}')`
        : "url('https://images.unsplash.com/photo-1524492412937-b28074a5d7da?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: 'white',
      textAlign: 'left',
      padding: isMobile ? '40px 20px' : '60px 30px',
      position: 'relative',
      borderRadius: '12px 12px 0 0',
    },
    bannerBefore: {
      content: "''",
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '12px 12px 0 0', 
    },
    bannerContent: {
      position: 'relative',
      zIndex: 1,
      paddingLeft:'20px',
    },
    bannerH1: {
      fontSize: '2.5em',
      marginBottom: '10px',
    },
    bannerP: {
      fontSize: '1.1em',
      marginBottom: '20px',
    },
    exploreButton: {
      backgroundColor: '#ffeb3b',
      color: '#333',
      border: 'none',
      paddingLeft: '20px',
      padding: '5px 20px',
      borderRadius: '5px',
      cursor: 'pointer',
      fontSize: '1em',
      fontWeight: 'bold',
      textDecoration: 'none',
      display: 'inline-block',
    },
    cardsOverlay: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: isMobile ? '-70px' : '-85px',
      zIndex: 2,
      display: 'flex',
      overflowX: 'auto',
      gap: isMobile ? '8px' : '12px',
      padding: '0 15%', // Add padding instead of using left/right positioning
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      WebkitOverflowScrolling: 'touch',
      maxWidth: '100%', // Ensure it doesn't exceed container width
      margin: '0 auto', // Center the scrollable area
    },
    destinationCard: {
      backgroundColor: '#fff',
      borderRadius: '8px', // Apply border radius to the card itself
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      width: isMobile ? '140px' : '170px', // Smaller cards on mobile
      height: isMobile ? '180px' : '220px', // Smaller height on mobile
      flex: '0 0 auto',
      position: 'relative', // Needed for absolute positioning of child
    },
    destinationCardImg: {
      width: '100%',
      height: '100%', // Make image fill the card
      display: 'block',
      objectFit: 'cover',
      border: '3px solid white', // Added white border
      boxSizing: 'border-box', // Ensure border is included in element dimensions
      borderRadius: '8px', // Match the rounded corners of the parent container
    },
    cardInfo: {
      position: 'absolute', // Position text over the image
      bottom: 0,
      left: 0,
      right: 0,
      background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%)',
      padding: '10px', // Adjusted padding
      textAlign: 'center',
      color: 'white', // Text color for contrast
      textShadow: '1px 1px 3px rgba(0, 0, 0, 0.7)', // Add text shadow for readability
      borderBottomLeftRadius: '8px', // Match parent border radius
      borderBottomRightRadius: '8px', // Match parent border radius
    },
    cardInfoH3: {
      marginTop: 0,
      marginBottom: '5px',
      fontSize: '1.1em', // Slightly adjusted size
      color: 'white', // Ensure heading is white (shadow applied from parent)
    },
    cardInfoP: {
      fontSize: '0.8em', // Slightly adjusted size
      color: '#eee', // Lighter color for subtext (shadow applied from parent)
      marginBottom: 0,
    },
    navigationArrow: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: 'white', // Solid white background
      border: 'none',
      borderRadius: '50%',
      width: isMobile ? '30px' : '40px', // Match other components
      height: isMobile ? '30px' : '40px', // Match other components
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      color: '#333',
      opacity: 1,
      zIndex: 10,
      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
      padding: 0, // Remove default button padding
    },
    navLeft: {
      left: isMobile ? '5%' : '12%', // Position relative to container edges
    },
    navRight: {
      right: isMobile ? '5%' : '12%', // Position relative to container edges
    },
    arrowIcon: {
      backgroundColor: '#FFD700', // Gold/yellow background like other components
      borderRadius: '50%',
      padding: isMobile ? '6px' : '10px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'white', // White icon color
      fontSize: '12px', // Adjust font size of icon
      width: '100%',
      height: '100%',
    },
    whatsappIcon: {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      backgroundColor: '#25d366',
      color: 'white',
      borderRadius: '50%',
      width: '50px',
      height: '50px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '2em',
      textDecoration: 'none',
      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
      zIndex: 1000,
    },
  };

  return (
    <div style={styles.outerContainer}>
      <div style={styles.container}>
        <div style={styles.banner}>
          <div style={styles.bannerBefore}></div>
          <div style={styles.bannerContent}>
            <h1 style={{...styles.bannerH1, fontSize: isMobile ? '1.8em' : '2.5em'}}>Explore India</h1>
            <p style={styles.bannerP}>A Journey Through Time, Colour And Culture</p>
            <button
              type="button"
              style={styles.exploreButton}
              onClick={() => {
                navigate('/destinations?location=india');
                window.scrollTo(0, 0); // Scroll to top after navigating
              }}
            >
              Explore
            </button>
          </div>
        </div>
      </div>

      {/* Position the navigation buttons outside the cards container */}
      <button 
        style={{...styles.navigationArrow, ...styles.navLeft}} 
        onClick={scrollLeft}
        aria-label="Scroll left"
      >
        <span style={styles.arrowIcon}>
          <i className="fas fa-chevron-left"></i>
        </span>
      </button>
      
      <button 
        style={{...styles.navigationArrow, ...styles.navRight}} 
        onClick={scrollRight}
        aria-label="Scroll right"
      >
        <span style={styles.arrowIcon}>
          <i className="fas fa-chevron-right"></i>
        </span>
      </button>

      {/* Cards container without the navigation arrows inside */}
      <div style={styles.cardsOverlay} ref={cardsContainerRef}>
        {/* Map over the India categories from the JSON data */}
        {categoriesData.India.map((destination, index) => (
          <div
            key={index} // Use index as key, or preferably a unique ID if available
            style={styles.destinationCard}
            onClick={() => handleCardClick(destination.name)}
            role="button"
            tabIndex={0}
          >
            <img src={destination.img} alt={destination.name} style={styles.destinationCardImg} />
            <div style={styles.cardInfo}>
              <h3 style={styles.cardInfoH3}>{destination.name}</h3>
              {/* <p style={styles.cardInfoP}>{destination.price}</p> */}
            </div>
          </div>
        ))}
        {/* End of Dynamic Destination Cards */}
      </div>
    </div>
  );
};

export default DesktopCategories;
